import React from 'react'

import { FormControl, FormHelperText, InputLabel, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { formField } from 'decorators'
import debounce from 'lodash/debounce'

import { input } from '@albacross/frontend-utils'

import { get } from 'utils/http'

type CustomerSelectProps = {
  value: { id: number; name: string }
  onBlur: (...args: any[]) => any
  onChange: (...args: any[]) => any
  onFocus: (...args: any[]) => any
  allowEmpty?: boolean
  className?: string
  disabled?: boolean
  errorId?: string | boolean
  fullWidth?: boolean
  label?: string
  warningId?: string | boolean
}

const CustomerSelect = (props: CustomerSelectProps) => {
  const { className, disabled, errorId, warningId, label, fullWidth = true, value, onFocus, onBlur } = props

  const [search, setSearch] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [options, setOptions] = React.useState<any[]>([value])

  React.useEffect(() => {
    let active = true

    const fetchCustomers = async (search: string) => {
      const response: any = await get('/management/customers', { query: { search } })
      const customers = response.items.map(({ id, name, client_id: clientId }: any) => ({ id, name, clientId }))

      if (active) {
        setOptions(customers)
      }
    }

    if (open) fetchCustomers(search)

    return () => {
      active = false
    }
  }, [search])

  const handleChange = (_event: any, newValue: any) => props.onChange(newValue)
  const handleInputChange = (_event: any, newInputValue: any) => setSearch(newInputValue)

  const getOptionLabel = ({ name, clientId }: any) => {
    if (!clientId) return name

    return `${name} - ${clientId}`
  }

  return (
    <FormControl className={className} disabled={disabled} error={!!errorId} fullWidth={fullWidth}>
      <InputLabel>{label}</InputLabel>
      <Autocomplete
        disableClearable
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
        id="customer-select"
        loading={false}
        onChange={handleChange}
        onClose={_e => {
          setOpen(false)
          onBlur()
        }}
        onInputChange={debounce(handleInputChange, 300)}
        onOpen={_e => {
          setOpen(true)
          onFocus(_e)
        }}
        open={open}
        options={options}
        renderInput={params => <TextField {...params} />}
        value={value}
      />
      {errorId && <FormHelperText error>{errorId}</FormHelperText>}
      {warningId && <FormHelperText sx={{ color: 'orange' }}>{warningId}</FormHelperText>}
    </FormControl>
  )
}

export default formField(input()(CustomerSelect))
